/**
 * ProvenanceBadge - Displays the origin/provenance of a document.
 * Shows where the document came from: Discovery, Upload, Auto-import, or Census.
 */

import { Upload, Search, RefreshCw, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

type Provenance =
  | "manual_upload"
  | "discovery"
  | "auto_import"
  | "census_first";

const PROVENANCE_CONFIG: Record<
  Provenance,
  { label: string; color: string; icon: typeof Upload }
> = {
  manual_upload: {
    label: "Upload",
    color: "bg-muted text-muted-foreground",
    icon: Upload,
  },
  discovery: {
    label: "Discovery",
    color: "bg-primary/10 text-primary",
    icon: Search,
  },
  auto_import: {
    label: "Auto-import",
    color: "bg-info/10 text-info",
    icon: RefreshCw,
  },
  census_first: {
    label: "Census",
    color: "bg-success/10 text-success",
    icon: Building2,
  },
};

interface ProvenanceBadgeProps {
  provenance: Provenance | string | null | undefined;
  className?: string;
}

export function ProvenanceBadge({
  provenance,
  className,
}: ProvenanceBadgeProps) {
  const config =
    PROVENANCE_CONFIG[(provenance as Provenance) || "manual_upload"];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn("gap-1 text-xs font-normal", config.color, className)}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}
